import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FirebasePerformanceTypes } from '@react-native-firebase/perf';
import { useStoreContext } from 'features/store';

import { IRestaurant } from '@rbi-ctg/store';
import {
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  ServiceMode,
  useOneStoreMenuMetadataQuery,
} from 'generated/graphql-gateway';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { MENU_API_URL } from 'remote/constants';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useServiceModeContext } from 'state/service-mode';
import { appVersion } from 'utils/app-version-info';
import { getDateInTimezone } from 'utils/dateTime';
import { getLanguage } from 'utils/intl/language';
import { fetch } from 'utils/network';
import { startTrace } from 'utils/performance/trace';
import { useMemoAll } from 'utils/use-memo-all';

import { findActiveDayPartIds } from './dayparts';
import {
  IIngredients,
  ILocalizedMenu,
  ILocalizedMenuEntry,
  IMainMenuNode,
  IndexedMenu,
} from './types';
import { IUnifiedMenuContext, UnifiedMenuContext } from './unified-menu-context';
import {
  createIndexedMenu,
  createIndexedOffers,
  createIndexedRewards,
  createMainMenuNode,
  getPosServiceMode,
} from './utils';

export const UnifiedMenuProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isEnabled = useEnableUnifiedMenu();
  const { serviceMode } = useServiceModeContext();
  const { store, availableDayParts } = useStoreContext();
  const [hasError, setHasError] = useState(false);

  /**
   * Store the last fetch time for each store menu, used to determine if the menu if we need to refetch
   * the full menu or keep the cached version. This being empty means that the menu has never been fetched
   * in the current session.
   */
  const lastFetchTime = useRef<Record<string, number>>({});
  const fetchId = useRef(0);

  const [menu, setMenu] = useState<ILocalizedMenu | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Get the last modified time of the menu and refetch if it has changed
  useOneStoreMenuMetadataQuery({
    variables: store?.number ? { storeId: store.number } : undefined,
    skip: !isEnabled || !store?.number,
    onCompleted: data => {
      if (data.OneStoreMenuMetadata?.lastModified) {
        const lastModified = Date.parse(data.OneStoreMenuMetadata.lastModified);
        if (lastModified > lastFetchTime.current[store?.number ?? '']) {
          fetchMenu();
        }
      }
    },
  });

  const indexedMenu: IndexedMenu<ILocalizedMenuEntry> = useMemo(
    () => (menu ? createIndexedMenu(menu) : IndexedMenu.EMPTY),
    [menu]
  );

  const indexedRewards: IndexedMenu<IRewardEntryDto> = useMemo(
    () => (menu ? createIndexedRewards(menu) : IndexedMenu.EMPTY),
    [menu]
  );
  const indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto> = useMemo(
    () => (menu ? createIndexedOffers(menu) : IndexedMenu.EMPTY),
    [menu]
  );

  const mainMenu = useMemo(
    () => maybeCreateMainMenu(menu, indexedMenu, serviceMode, store, availableDayParts),
    [availableDayParts, indexedMenu, menu, serviceMode, store]
  );

  const fetchMenu = useCallback(async () => {
    let trace: FirebasePerformanceTypes.Trace | null = null;
    const currentFetchId = ++fetchId.current;
    try {
      setIsLoading(true);

      // new high performance fetch that hits s3 directly which is pre BR compressed
      trace = startTrace(`fetch_1m_s3`, { storeId: store?.number ?? '' });
      const result = await fetch<ILocalizedMenu>(`${MENU_API_URL}/${store?.number}-gql.json`, {
        method: 'GET',
        headers: {
          'x-ui-version': appVersion,
        },
        // unfortunately this adds ~15ms in debug mode (less in release mode probably)
        // but its better than updating every `.locale` in the app to be dynamic
        textTransform: text => text.replaceAll(`"${getLanguage()}":"`, '"locale":"'),
      });

      // ignore stale responses from previous store changes
      if (currentFetchId !== fetchId.current) {
        return;
      }

      setMenu(result);
      lastFetchTime.current[store?.number ?? ''] = Date.now();
    } catch (error) {
      if (currentFetchId !== fetchId.current) {
        return;
      }
      setHasError(true);
    } finally {
      trace?.stop();
      if (currentFetchId === fetchId.current) {
        setIsLoading(false);
      }
    }
  }, [store?.number]);

  useEffect(() => {
    if (isEnabled && store?.number) {
      fetchMenu();
    }
  }, [isEnabled, store?.number, fetchMenu]);

  const value: IUnifiedMenuContext = useMemoAll({
    rawMenu: menu,
    mainMenu: mainMenu?.children ?? EMPTY_MENU,
    indexedMenu,
    indexedRewards,
    indexedOffers,
    isLoading,
    refetchMenu: fetchMenu,
    ingredients: (menu?.ingredients as IIngredients[]) ?? [],
    hasError,
  });

  return <UnifiedMenuContext.Provider value={value}>{children}</UnifiedMenuContext.Provider>;
};

const EMPTY_MENU: IMainMenuNode[] = [];

function maybeCreateMainMenu(
  menu: ILocalizedMenu | undefined,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry> | undefined,
  serviceMode: ServiceMode | null,
  store: IRestaurant | undefined,
  availableDayParts: IDayPartBoundary[] | undefined
): IMainMenuNode | undefined {
  if (indexedMenu && menu && serviceMode && store) {
    const now = store.timezone ? getDateInTimezone(store.timezone) : new Date();
    // TODO In future there should be only one source for this: availableDayParts
    const activeDayPartIds = availableDayParts
      ? availableDayParts.map(dp => dp.key)
      : findActiveDayPartIds(menu.dayParts, now);
    return createMainMenuNode(indexedMenu, menu.rootMenuEntryId, {
      posServiceMode: getPosServiceMode(serviceMode),
      dayPartIds: activeDayPartIds,
    });
  }
  return undefined;
}
